import {
  CARROT_ROTATION_SPEED,
  Core,
  CoreConstructorParameters,
  CoreInitParameters,
  RABBIT_STATE,
} from '@/modules/advergaming/core'
import {
  AmbientLight,
  AnimationClip,
  AnimationMixer,
  Box3,
  Group,
  LoopOnce,
  MathUtils,
  Object3D,
  Scene,
  Vector3,
} from 'three'
import { lerpDifficultyProperty } from '@/modules/advergaming/utils'
import gsap from 'gsap'

// Range between which interpolate camera z position
const SCREEN_WIDTH_MIN = 0
const SCREEN_WIDTH_MAX = 1000

interface SectionParameters {
  origin: Vector3
  rockPrefabs: Object3D[]
  brokenPrefab: Object3D
  grassPrefab: Object3D
  carrotPrefab: Object3D
  combo: number
}

type Key = { isDown: boolean; isJustDown: boolean }

const KEY = {
  LEFT: 'ArrowLeft',
  RIGHT: 'ArrowRight',
} as const

const PLATFORM_MAX_DISTANCE = 4.5
const PLATFORM_WIDTH = 2
const HALF_PLATFORM_WIDTH = PLATFORM_WIDTH / 2
const PLATFORM_Y_SCALE = 1
const WORLD_WIDTH = 15
const SECTION_TRIGGER_HEIGHT = 30
const MAX_SAFE_PATH_PLATFORM_SPAWNING_ATTEMPTS = 20
const PLATFORMS_DEPTH_SCALE = 1
const DIRECTIONAL_LIGHT_POSITION = new Vector3(-12, 0, 10)
const PLAYER_SCALE = 1.5
const CARROT_SCALE = 1.5

const PARAMS = {
  player: {
    acceleration: 1,
    friction: 0.25,
    maxVelocity: 0.2,
    gravity: -0.015,
    gameOverYThreshold: 0,
    hitboxRadius: 0.75 * PLAYER_SCALE,
  },
  camera: {
    yOffset: {
      min: 17,
      max: 7.5,
    },
    lerp: 0.1,
    fov: 7,
  },
  carrot: {
    yOffset: 0.5,
    chance: 0.5,
  },
  collision: {
    platformMinDistance: 2,
  },
  platforms: {
    safeDistance: 6,
  },
  difficulty: {
    lowDifficulty: {
      minCombo: 5,
      randomPlatformsCount: 50,
      breakablePlatformChance: 0,
      breakableRandomPlatformChance: 0.25,
      maxDistanceX: 4.5,
      maxDistanceY: 6.5,
      jump: 0.475,
    },
    highDifficulty: {
      minCombo: 40,
      randomPlatformsCount: 0,
      breakablePlatformChance: 1,
      breakableRandomPlatformChance: 1,
      maxDistanceX: 8.5,
      maxDistanceY: 6.5,
      jump: 0.475,
    },
  },
  respaw: {
    delay: 1,
  },
  ears: {
    minY: -0.5,
    maxY: 0.5,
    minRot: 1,
    maxRot: -0.65,
  },
}

// https://www.jeffreythompson.org/collision-detection/line-line.php
function lineLine(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  x3: number,
  y3: number,
  x4: number,
  y4: number
) {
  // calculate the distance to intersection point
  const uA =
    ((x4 - x3) * (y1 - y3) - (y4 - y3) * (x1 - x3)) /
    ((y4 - y3) * (x2 - x1) - (x4 - x3) * (y2 - y1))
  const uB =
    ((x2 - x1) * (y1 - y3) - (y2 - y1) * (x1 - x3)) /
    ((y4 - y3) * (x2 - x1) - (x4 - x3) * (y2 - y1))

  // if uA and uB are between 0-1, lines are colliding
  if (uA >= 0 && uA <= 1 && uB >= 0 && uB <= 1) {
    return true
  }
  return false
}

// https://www.jeffreythompson.org/collision-detection/point-rect.php
function pointRect(
  px: number,
  py: number,
  rx: number,
  ry: number,
  rw: number,
  rh: number
) {
  // is the point inside the rectangle's bounds?
  if (
    px >= rx && // right of the left edge AND
    px <= rx + rw && // left of the right edge AND
    py >= ry && // below the top AND
    py <= ry + rh
  ) {
    // above the bottom
    return true
  }
  return false
}

const randomRotation = () =>
  [0, MathUtils.degToRad(90), MathUtils.degToRad(180), MathUtils.degToRad(270)][
    MathUtils.randInt(0, 3)
  ]

class Platform extends Object3D {
  public readonly isTrap: boolean = false
  protected depthScaleHolder = new Group()
  public grass: Object3D

  constructor(object: Object3D, grass: Object3D) {
    super()

    object.rotation.y = randomRotation()

    this.depthScaleHolder.add(object)
    this.depthScaleHolder.scale.z = PLATFORMS_DEPTH_SCALE

    this.add(this.depthScaleHolder)

    this.grass = grass
    this.grass.rotation.y = randomRotation()
    object.add(this.grass)
  }

  public static isTrap(
    platform: Platform | BrokenPlatform
  ): platform is BrokenPlatform {
    return (platform as BrokenPlatform).isTrap === true
  }
}

class BrokenPlatform extends Platform {
  public readonly isTrap = true
  public mixer: AnimationMixer
  public isBroken = false

  constructor(object: Object3D, grass: Object3D) {
    super(object, grass)

    this.mixer = new AnimationMixer(object)

    object.rotation.y = Math.random() >= 0.5 ? 0 : MathUtils.degToRad(180)
  }

  public break(clips: AnimationClip[]) {
    this.isBroken = true

    clips.forEach(clip => {
      const action = this.mixer.clipAction(clip)
      action.clampWhenFinished = true
      action.setLoop(LoopOnce, 1).play()
    })

    gsap.to(this.grass.scale, {
      y: 0,
      ease: 'power4.out',
      duration: 0.5,
      onComplete: () => {
        this.grass.removeFromParent()
      },
    })
  }
}

class Section extends Group {
  public isLastSection = true
  public platforms: Platform[] = []
  public carrots: Object3D[] = []

  private PLATFORM_PREFABS: Object3D[]
  private BROKEN_PLATFORM_PREFAB: Object3D
  private CARROT_PREFAB: Object3D
  private GRASS_PREFAB: Object3D

  public box = new Box3()

  constructor(parameters: SectionParameters) {
    super()

    this.PLATFORM_PREFABS = parameters.rockPrefabs
    this.BROKEN_PLATFORM_PREFAB = parameters.brokenPrefab
    this.CARROT_PREFAB = parameters.carrotPrefab
    this.GRASS_PREFAB = parameters.grassPrefab

    this.generateSafePath(parameters.origin, 10, parameters.combo)

    // Compute section bounding box AFTER having generated platform path.
    this.box.setFromPoints(this.platforms.map(platform => platform.position))

    // In addition to safe path, depending on difficulty level (combo), add platforms at random positions
    const randomPlatformsCount = lerpDifficultyProperty(
      PARAMS,
      'randomPlatformsCount',
      parameters.combo
    )

    if (randomPlatformsCount) {
      this.addRandomPlatforms(randomPlatformsCount, parameters.combo)
    }
  }

  private generateSafePath(
    origin: Vector3,
    platformsCount: number,
    combo: number
  ) {
    const position = origin.clone()
    const tempPosition = position.clone()

    for (let i = 0; i < platformsCount; i++) {
      let success = false
      let attempts = 0

      // It is possible that the safe path algorithm spawn platform really close to each other.
      // For exemple: platform 1, then spawn platform 2 to the right, then spawn platform 3 to the left. Platform 1 and 3 ends up really close to one another.
      // We can't simply disregard platform 3 as it would break the safe path.
      // Instead, if new platform doesn't pass proximity check, we'll retry until we found one position that fits.
      // If we don't manage to find a suitable position within MAX_ATTEMPTS, we'll disregard platform...
      while (!success && attempts < MAX_SAFE_PATH_PLATFORM_SPAWNING_ATTEMPTS) {
        // Do not update position vector until we're sure that the position is safe (after proximity check).
        // Until then, use temporary tempPosition instead.
        tempPosition.copy(position)

        const randomAngle = MathUtils.randFloatSpread(MathUtils.degToRad(90))

        const maxDistanceX = lerpDifficultyProperty(
          PARAMS,
          'maxDistanceX',
          combo
        )

        const maxDistanceY = lerpDifficultyProperty(
          PARAMS,
          'maxDistanceY',
          combo
        )

        let newX = tempPosition.x + Math.sin(randomAngle) * maxDistanceX

        let newY = tempPosition.y + Math.cos(randomAngle) * maxDistanceY

        // Check for the horizontal axis, if position is out of world bounds, invert X offset
        if (
          newX >= WORLD_WIDTH / 2 - HALF_PLATFORM_WIDTH ||
          newX <= -WORLD_WIDTH / 2 + HALF_PLATFORM_WIDTH
        ) {
          newX = tempPosition.x - Math.sin(randomAngle) * PLATFORM_MAX_DISTANCE
        }

        tempPosition.x = newX
        tempPosition.y = newY

        const isAboveFloor = tempPosition.y > 2 // Prevent blocs merging into floor
        if (!this.platformProximityCheck(tempPosition) && isAboveFloor) {
          position.copy(tempPosition)

          const breakable =
            Math.random() <=
            lerpDifficultyProperty(PARAMS, 'breakablePlatformChance', combo)

          this.addPlatform(position, true, breakable)

          success = true
        }

        attempts++
      }
    }

    console.groupEnd()
  }

  private addRandomPlatforms(platformsCount: number, combo: number) {
    const position = new Vector3(0, 0, 0)

    for (let i = 0; i < platformsCount; i++) {
      // Set position to a random value
      // x: anywhere within world bounds
      // y: limited to current section bounds
      position.set(
        MathUtils.randFloat(
          -WORLD_WIDTH / 2 + HALF_PLATFORM_WIDTH,
          WORLD_WIDTH / 2 - HALF_PLATFORM_WIDTH
        ),
        MathUtils.randFloat(this.box.min.y, this.box.max.y),
        0
      )

      // Prevent randoms from spawing too close to other platforms
      // in order to avoid clusters and overlaps.
      if (this.platformProximityCheck(position)) {
        continue
      }

      const breakable =
        Math.random() <
        lerpDifficultyProperty(PARAMS, 'breakableRandomPlatformChance', combo)

      this.addPlatform(position, false, breakable)
    }
  }

  /**
   * Returns true if position distance to any platform is below threshold.
   */
  private platformProximityCheck(position: Vector3) {
    return this.platforms.some(
      platform =>
        platform.position.distanceTo(position) < PARAMS.platforms.safeDistance
    )
  }

  private addPlatform(position: Vector3, isSafePath: boolean, isTrap: boolean) {
    const platform = (() => {
      if (isTrap) {
        return new BrokenPlatform(
          this.BROKEN_PLATFORM_PREFAB.clone(),
          this.GRASS_PREFAB.clone()
        )
      }

      return new Platform(
        this.PLATFORM_PREFABS[MathUtils.randInt(0, 3)].clone(),
        this.GRASS_PREFAB.clone()
      )
    })()

    platform.position.copy(position)

    if (Math.random() < PARAMS.carrot.chance) {
      this.addCarrotAt(position)
    }

    platform.userData.isSafePath = isSafePath

    this.platforms.push(platform)

    this.add(platform)
  }

  private addCarrotAt(position: Vector3) {
    const carrot = this.CARROT_PREFAB.clone()

    carrot.scale.setScalar(CARROT_SCALE)

    carrot.position.set(
      position.x,
      position.y + PARAMS.carrot.yOffset,
      position.z
    )

    this.carrots.push(carrot)

    this.add(carrot)
  }
}

class CarrotJump extends Core {
  protected cameraPositionMobile = new Vector3(0, 0, 350)
  protected cameraPositionDesktop = new Vector3(0, 0, 164)

  // Second render pass which renders only the player on top of everything else.
  // This bypass depth issues between the player, its outline and the blocks.
  protected playerScene!: Scene
  protected player!: Object3D
  protected playerVelocity = new Vector3()
  protected playerLeftEar!: Object3D
  protected playerRightEar!: Object3D
  protected playerBounceTween!: gsap.core.Tween

  private PLATFORM_PREFABS: Object3D[] = []
  private BROKEN_PLATFORM_PREFAB = new Group()
  private CARROT_PREFAB = new Group()
  private GRASS_PREFAB = new Group()

  // Array of clones use as floor below initial section.
  // The objects contained in this array are going to be treated as regular platforms in the collision detection phase.
  private floor: Platform[] = []

  private sections: Section[] = []

  // Y position platform of the highest platform layer has bounced on. Used to update camera target.
  private highestPlatformY = 0

  // Game over if player y position goes below this value.
  // It is re-computed at every update based on the camera view frustum.
  private gameOverIfBelow = -Infinity

  public keys: Record<(typeof KEY)[keyof typeof KEY], Key> = {
    ArrowLeft: {
      isDown: false,
      isJustDown: false,
    },
    ArrowRight: {
      isDown: false,
      isJustDown: false,
    },
  }
  private pointerIsDown = false
  private BCR!: DOMRect

  constructor(params: CoreConstructorParameters) {
    super(params)
  }

  async init(params: CoreInitParameters) {
    super.init(params)

    this.dirLight.position.copy(DIRECTIONAL_LIGHT_POSITION)
    this.antiLight.position.copy(this.dirLight.position)

    this.camera.fov = PARAMS.camera.fov
  }

  protected afterInit() {
    // We need to have the same lightning setup in both scene.
    this.playerScene = this.scene.clone()

    // Need to disable core renderer autoclear for the two render passes system to work.
    // Clearing is managed manually from the render loop.
    this.renderer.autoClear = false

    this.updateBCR()

    this.initPlatformPrefab()

    this.initFloorSection()

    this.initPlayer()

    this.addSection()

    this.onKeyDown = this.onKeyDown.bind(this)
    this.onKeyUp = this.onKeyUp.bind(this)

    window.addEventListener('keydown', this.onKeyDown)
    window.addEventListener('keyup', this.onKeyUp)
  }

  protected initLights(): void {
    super.initLights()

    // Don't want antilight for this specific game
    this.scene.remove(this.antiLight)
    this.dirLight.intensity = 1

    this.dirLight.position.copy(DIRECTIONAL_LIGHT_POSITION)

    this.scene.add(new AmbientLight(0xffffff, 0.25))
  }

  /**
   * Called upon game over.
   * Remove existing everything and reset all logic related variables.
   * Restart game from scratch.
   */
  private reset() {
    this.player.visible = true

    this.rabbitState = RABBIT_STATE.ALIVE

    this.store.$combo.set(0)

    this.sections.forEach(section => {
      this.scene.remove(section)
    })
    this.sections = []

    this.camera.position.copy(this.getCameraPosition())
    this.camera.position.y += this.cameraYOffset

    this.player.position.set(0, 0, 0)

    this.highestPlatformY = 0

    this.gameOverIfBelow = -Infinity

    this.addSection()
  }

  /**
   * Creates a unique kind of section, instead of having a vertical path of platform,
   * it is just an horizontal line of platforms.
   */
  private initFloorSection() {
    // Invisible solid ground
    for (let x = -16; x < 20; x += PLATFORM_WIDTH) {
      const platform = new Platform(
        this.PLATFORM_PREFABS[0].clone(),
        this.GRASS_PREFAB.clone()
      )
      platform.visible = false
      platform.position.set(x, 0, 0)
      this.floor.push(platform)
    }

    // fake cosmetic wide platform
    this.scene.add(this.assets.get('rock-floor')!.clone())
  }

  private initPlayer() {
    this.player = this.assets.get('rabbit')!
    this.playerLeftEar = this.player.getObjectByName('earLeft')!
    this.playerRightEar = this.player.getObjectByName('earRight')!

    // Store the previous position somewhere.
    // Here it's stored in the object3d userData but it could be a property of game object or a property of a custom Player class.
    // It is going to be used to perfom line/line collision detection.
    this.player.userData.previousPosition = this.player.position.clone()
    this.playerScene.add(this.player)

    this.playerBounceTween = gsap.fromTo(
      this.player.scale,
      {
        x: 1.5 * PLAYER_SCALE,
        y: 0.5 * PLAYER_SCALE,
      },
      {
        x: 1 * PLAYER_SCALE,
        y: 1 * PLAYER_SCALE,
        ease: 'power4.out',
        duration: 0.5,
        paused: true,
      }
    )
  }

  private initPlatformPrefab() {
    this.PLATFORM_PREFABS.push(this.assets.get('rock-1')!.clone())
    this.PLATFORM_PREFABS.push(this.assets.get('rock-2')!.clone())
    this.PLATFORM_PREFABS.push(this.assets.get('rock-3')!.clone())
    this.PLATFORM_PREFABS.push(this.assets.get('rock-4')!.clone())

    this.GRASS_PREFAB.add(this.assets.get('grass')!.clone())

    const brokenBlock = this.assets.get('block-broken')!.clone()
    brokenBlock.scale.set(1, PLATFORM_Y_SCALE, 1)
    this.BROKEN_PLATFORM_PREFAB.add(brokenBlock)

    this.CARROT_PREFAB.add(this.assets.get('carrot')!.clone())
  }

  private addSection() {
    const section = new Section({
      origin: this.getLastPlatformPosition(),
      rockPrefabs: this.PLATFORM_PREFABS,
      brokenPrefab: this.BROKEN_PLATFORM_PREFAB,
      carrotPrefab: this.CARROT_PREFAB,
      grassPrefab: this.GRASS_PREFAB,
      combo: this.store.$combo.get(),
    })

    if (this.sections.length > 0) {
      this.sections[this.sections.length - 1].isLastSection = false
    }

    this.sections.push(section)

    this.scene.add(section)
  }

  private getLastPlatformPosition() {
    // Prevent very first platforms to spawn right over player
    if (this.sections.length < 1) {
      return Math.random() > 0.5 ? new Vector3(-5, 0, 0) : new Vector3(5, 0, 0)
    }

    const section = this.sections[this.sections.length - 1]
    const safePlatforms = section.platforms.filter(
      platform => platform.userData.isSafePath
    )

    if (safePlatforms.length < 1) {
      return new Vector3()
    }

    const platform = safePlatforms[safePlatforms.length - 1]

    return platform.position
  }

  protected onKeyDown(e: KeyboardEvent) {
    if (
      this.isContainerWithinViewport &&
      (e.code === KEY.LEFT || e.code === KEY.RIGHT)
    ) {
      if (!this.keys[e.code].isDown) {
        this.store.$interactedWith.set(true)
        this.keys[e.code].isDown = true
        this.keys[e.code].isJustDown = true
        this.store.globalPinia.clicks += 1
      }
    }
  }

  protected onKeyUp(e: KeyboardEvent) {
    if (e.code === KEY.LEFT || e.code === KEY.RIGHT) {
      this.keys[e.code].isDown = false
    }
  }

  onRender(_interp: number, deltaTime: number) {
    this.getAnimatedPlatforms().forEach(p => {
      if (Platform.isTrap(p)) {
        p.mixer.update(deltaTime / 1000)
      }
    })

    this.sections.forEach(s => {
      s.carrots.forEach(c => {
        c.rotation.y += deltaTime * CARROT_ROTATION_SPEED
      })
    })

    // Do not call super.onRender
    if (this.useDebugCamera) {
      this.debugCameraControls.update()
      this.renderer.clear()
      this.renderer.render(this.scene, this.debugCamera)
    } else {
      this.renderer.clear()
      this.renderer.render(this.scene, this.camera)
      this.renderer.clearDepth()
      this.renderer.render(this.playerScene, this.camera)
    }
  }

  protected onFixedUpdate(timestep: number) {
    this.store.globalPinia.duration.carrotJump += timestep

    if (!this.player || this.rabbitState !== RABBIT_STATE.ALIVE) {
      return
    }

    this.checkGameOver()
    this.checkNewSection()
    this.updatePlayerPosition()
    this.checkPlayerCarrotCollision()
    this.checkPlayerPlatformCollision()
    this.updatePlayerEars()
    this.updateCameraPosition()
    this.updateGameOverThreshold()
    this.disposeOldSections()
  }

  protected onResize() {
    this.setSize()

    const { width, height } = this.getDimensions()
    const aspect = width / height

    this.camera.aspect = aspect
    this.camera.updateProjectionMatrix()

    if (this.debugCamera) {
      this.debugCamera.aspect = aspect
      this.debugCamera.updateProjectionMatrix()
    }

    this.updateBCR()
  }

  private checkGameOver() {
    if (this.player.position.y < this.gameOverIfBelow) {
      this.rabbitState = RABBIT_STATE.DEAD

      this.player.visible = false

      this.cameraShake(0.1)

      this.particlesDeadRabbit(this.player.position, PLAYER_SCALE)

      window.setTimeout(() => {
        this.reset()
      }, PARAMS.respaw.delay * 1000)
    }
  }

  private updatePlayerPosition() {
    if (this.keys.ArrowLeft.isDown) {
      this.playerVelocity.x -= PARAMS.player.acceleration
    } else if (this.keys.ArrowRight.isDown) {
      this.playerVelocity.x += PARAMS.player.acceleration
    } else {
      // Apply friction to velocity (=== lerp x velocity toward 0)
      this.playerVelocity.x = MathUtils.lerp(
        this.playerVelocity.x,
        0,
        PARAMS.player.friction
      )
    }

    this.playerVelocity.x = MathUtils.clamp(
      this.playerVelocity.x,
      -PARAMS.player.maxVelocity,
      PARAMS.player.maxVelocity
    )

    // Apply gravity
    this.playerVelocity.y += PARAMS.player.gravity

    // Update position by adding velocity
    this.player.userData.previousPosition.copy(this.player.position)
    this.player.position.add(this.playerVelocity)

    // Horizontal clamp
    this.player.position.x = MathUtils.clamp(
      this.player.position.x,
      -WORLD_WIDTH / 2,
      WORLD_WIDTH / 2
    )
  }

  protected updatePlayerEars() {
    const rotation = MathUtils.mapLinear(
      this.playerVelocity.y,
      PARAMS.ears.minY,
      PARAMS.ears.maxY,
      PARAMS.ears.minRot,
      PARAMS.ears.maxRot
    )

    this.playerLeftEar.rotation.x = rotation
    this.playerRightEar.rotation.x = rotation
  }

  private checkPlayerPlatformCollision() {
    // If player is going upward it will go through platforms.
    // Only perform platform collision detection if going downward.
    if (Math.sign(this.playerVelocity.y) === 1) {
      return
    }

    this.getPlatforms().forEach(platform => {
      // Ignore already broken platforms
      if (Platform.isTrap(platform) && platform.isBroken) {
        return
      }

      // Disregard platform that are below player. It is impossible that they are colliding.
      // Note that in case of high velocity, it is possible that we detect a collision a platform which position is actually ABOVE player.
      // This is why we test using line/line.
      if (platform.position.y < this.player.position.y) {
        return
      }

      // Disregard platform if distance is higher than threshold.
      if (
        this.player.position.distanceTo(platform.position) >
        PARAMS.collision.platformMinDistance
      ) {
        return
      }

      // Perform lineline collision detection otherwise it is easy for player to go through platform without triggering collision due to high velocity
      const radius = PARAMS.player.hitboxRadius / 2
      const collision = lineLine(
        this.player.position.x,
        this.player.position.y - radius,
        this.player.userData.previousPosition.x,
        this.player.userData.previousPosition.y + radius,
        platform.position.x - PLATFORM_WIDTH / 2 - radius,
        platform.position.y,
        platform.position.x + PLATFORM_WIDTH / 2 + radius,
        platform.position.y
      )

      if (collision) {
        this.onPlayerPlatformCollision(platform)
      }
    })
  }

  private checkPlayerCarrotCollision() {
    this.getCarrots().forEach(carrot => {
      // Disregard carrot if distance is higher than threshold.
      if (
        this.player.position.distanceTo(carrot.position) >
        PARAMS.player.hitboxRadius
      ) {
        return
      }

      // Disregard if not visible (ie: already collected)
      // TODO: If we know the carrot is not collectoble, we should not be testing it at all. Find a more performant way of doing this.
      if (!carrot.visible) {
        return
      }

      this.onPlayerCarrotCollision(carrot)
    })
  }

  private onPlayerPlatformCollision(platform: Platform) {
    // On trap (breakable) platforms, player bounce once then platform disapear.
    if (Platform.isTrap(platform)) {
      platform.break(this.animationClips.brokenBlock)
    }

    this.particlesDustPoof(this.player.position, undefined, PLAYER_SCALE)

    this.playerVelocity.y = lerpDifficultyProperty(
      PARAMS,
      'jump',
      this.store.$combo.get()
    )

    if (platform.position.y > this.highestPlatformY) {
      this.highestPlatformY = platform.position.y
    }

    this.playerBounceTween.restart()
  }

  private onPlayerCarrotCollision(carrot: Object3D) {
    carrot.visible = false

    this.particlesCarrot(carrot.position, undefined, 1.25) // Note: CARROT_SCALE is a bit too dramatic here

    this.store.$score.set(this.store.$score.get() + 1)
    this.store.$combo.set(this.store.$combo.get() + 1)
  }

  private updateBCR() {
    this.BCR = this.containerEl.getBoundingClientRect()
  }

  /**
   * Returns flat array of platforms.
   */
  private getPlatforms() {
    return [
      ...this.floor,
      ...this.sections.flatMap(section => section.platforms),
    ]
  }

  /**
   * Returns flat array of platforms that needs to be animated (only broken trap platforms).
   */
  private getAnimatedPlatforms() {
    return this.sections.flatMap(section =>
      section.platforms.filter(p => Platform.isTrap(p) && p.isBroken)
    )
  }

  /**
   * Returns flat array of carrots.
   */
  private getCarrots() {
    return this.sections.flatMap(section => section.carrots)
  }

  /**
   * Will generate a new section if the player y position is above threshold.
   */
  private checkNewSection() {
    const lastSection = this.sections.find(section => section.isLastSection)
    if (!lastSection) {
      return
    }

    if (
      this.player.position.y >
      lastSection.box.max.y - SECTION_TRIGGER_HEIGHT
    ) {
      this.addSection()
    }
  }

  /**
   * Lerp camera y position toward desired new position.
   * Target position is the highest platform the player has interacted with + offset.
   */
  private updateCameraPosition() {
    const y = MathUtils.lerp(
      this.camera.position.y,
      this.highestPlatformY + this.cameraYOffset,
      PARAMS.camera.lerp
    )

    const z = MathUtils.mapLinear(
      this.clampedWidth,
      SCREEN_WIDTH_MIN,
      SCREEN_WIDTH_MAX,
      this.cameraPositionMobile.z,
      this.cameraPositionDesktop.z
    )

    this.camera.position.set(0, y, z)

    this.dirLight.position.y =
      this.camera.position.y + DIRECTIONAL_LIGHT_POSITION.y

    this.dirLight.target.position.y = this.camera.position.y

    this.dirLight.target.updateMatrixWorld()
  }

  get clampedWidth() {
    return MathUtils.clamp(this.BCR.width, SCREEN_WIDTH_MIN, SCREEN_WIDTH_MAX)
  }

  get cameraYOffset() {
    return MathUtils.mapLinear(
      this.clampedWidth,
      SCREEN_WIDTH_MIN,
      SCREEN_WIDTH_MAX,
      PARAMS.camera.yOffset.min,
      PARAMS.camera.yOffset.max
    )
  }

  /**
   * Update the y value below which player position will trigger game over.
   * It is updated every frame to the position of the bottom edge of camera frustum + threshold.
   */
  private updateGameOverThreshold() {
    // No need to compute frustum height every frame, this is only needed upon resize.
    const vFov = (this.camera.fov * Math.PI) / 180
    const viewFrustumHeight = 2 * Math.tan(vFov / 2) * this.camera.position.z

    this.gameOverIfBelow =
      this.camera.position.y -
      viewFrustumHeight / 2 -
      PARAMS.player.gameOverYThreshold
  }

  /**
   * Dispose of previous sections as the player goes up and they leave the viewport.
   */
  private disposeOldSections() {
    for (let i = this.sections.length - 1; i >= 0; i--) {
      const section = this.sections[i]

      if (section.box && section.box.max.y <= this.gameOverIfBelow) {
        this.scene.remove(section)
        this.sections.splice(i, 1)
      }
    }
  }

  protected initDebug(): void {
    super.initDebug()

    if (!this.debug) {
      return
    }

    const player = this.pane.addFolder({
      title: 'Player',
    })

    player.addBinding(PARAMS.player, 'acceleration', {
      min: 0,
      max: 5,
      step: 0.01,
    })

    player.addBinding(PARAMS.player, 'maxVelocity', {
      min: 0,
      max: 1,
      step: 0.01,
    })

    player.addBinding(PARAMS.player, 'friction', {
      min: 0,
      max: 10,
      step: 0.01,
    })

    player.addBinding(PARAMS.player, 'gravity', {
      min: -2,
      max: 0,
      step: 0.01,
    })

    const camera = this.pane.addFolder({
      title: 'Camera',
    })

    camera.addBinding(PARAMS.camera, 'lerp', {
      min: 0,
      max: 1,
    })

    camera.addBinding(this.camera.position, 'z', {
      step: 1,
    })

    camera
      .addBinding(PARAMS.camera, 'fov', {
        min: 1,
        max: 100,
      })
      .on('change', () => {
        this.camera.fov = PARAMS.camera.fov
        this.camera.updateProjectionMatrix()
      })

    const ears = this.pane.addFolder({
      title: 'Ears',
    })
    ears.addBinding(PARAMS.ears, 'minY', {
      min: -1,
      max: 1,
      step: 0.001,
    })
    ears.addBinding(PARAMS.ears, 'maxY', {
      min: -1,
      max: 1,
      step: 0.001,
    })
    ears.addBinding(PARAMS.ears, 'minRot', {
      min: -1,
      max: 1,
      step: 0.001,
    })
    ears.addBinding(PARAMS.ears, 'maxRot', {
      min: -1,
      max: 1,
      step: 0.001,
    })
  }

  public dispose() {
    window.removeEventListener('keydown', this.onKeyDown)
    window.removeEventListener('keyup', this.onKeyUp)
  }
}

export { CarrotJump }
